import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../src/contexts/AuthContext';
import { api } from '../../src/services/api';
import { supabase } from '../../src/lib/supabase';
import { MOSTRAR_ITENS_V2_EM_DISCUSSAO } from '../../src/lib/politicaComercial';
import { Profile, UserRole } from '../../types';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { Shield, Search, MoreVertical, Loader2, Plus, Trash2, Ban, KeyRound, CheckCircle, Activity, Users, Check, Building2, Send } from 'lucide-react';
import { useToast } from '../../src/contexts/ToastContext';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import CreateUserModal from '../../src/components/CreateUserModal';
import { DEPARTAMENTOS, DEPT_COLOR, FUNCOES_COMERCIAIS, ROTULO_FUNCAO_COMERCIAL, ehDoComercial, funcaoComercial, type FuncaoComercial } from '../../src/lib/departamentos';
import { gerenteAlcanca, PAPEIS_ATRIBUIVEIS_PELO_GERENTE } from '../../src/lib/gerenciaComercial';
import PermissionsModal from './PermissionsModal';
import PendenciasUsuarioModal, { type ResultadoSaida } from './PendenciasUsuarioModal';
import type { AcaoSaida } from '../../src/lib/pendenciasUsuario';
import { DEFINICOES_PERFIL, PERFIS_VISAO_GERAL, ehPerfilVisaoGeral, perfilAutomatico } from '../../src/lib/visaoGeral/perfis';
import { propsFundoModal } from '../../src/lib/backdropModal';

const CORP_EMAIL_DOMAIN = '@vigorenergy.com.br';

const toWhatsappDigits = (value: string) => value.replace(/\D/g, '').slice(0, 11);

const formatWhatsapp = (value: string) => {
    const digits = toWhatsappDigits(value);

    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const UserManagement = () => {
    const { profile } = useAuth();
    const [users, setUsers] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    // Dept dropdown open state per row
    const [openDeptId, setOpenDeptId] = useState<string | null>(null);

    // Actions Dropdown State
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Create User Modal State
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState<Profile | null>(null);
    const [editForm, setEditForm] = useState({ full_name: '', whatsapp: '', job_title: '', email_corporativo: '', email_pessoal: '', cpf: '', captado_por_id: '', parceiro_id: '' });
    const [savingEdit, setSavingEdit] = useState(false);

    // Permissions Modal State
    const [permissionsModalUser, setPermissionsModalUser] = useState<Profile | null>(null);

    // Janela "o que fazer com as pendências" ao inativar/excluir (ou depois, para quem já saiu)
    const [saida, setSaida] = useState<{ usuario: Profile; acao: AcaoSaida } | null>(null);

    const { addToast } = useToast();
    const [confirmationModal, setConfirmationModal] = useState<{
        isOpen: boolean;
        title: string;
        description: string;
        onConfirm: () => void;
        isLoading: boolean;
        variant?: 'danger' | 'warning' | 'info';
        confirmText?: string;
    }>({
        isOpen: false,
        title: '',
        description: '',
        onConfirm: () => { },
        isLoading: false
    });


    useEffect(() => {
        loadUsers();

        // Close menu when clicking outside
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setOpenMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadUsers = async () => {
        try {
            const data = await api.auth.listProfiles();
            setUsers(data);
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (userId: string, newRole: UserRole) => {
        const targetUser = users.find(u => u.id === userId);
        if (!targetUser || !canEditUserData(targetUser)) {
            addToast('Você não tem permissão para alterar a função deste usuário.', 'error');
            return;
        }

        setUpdating(userId);
        try {
            const { data, error } = await supabase.functions.invoke('manage-users', {
                body: { action: 'update_user_role', userId, newRole }
            });

            if (error) throw error;
            if (data && data.success === false) {
                throw new Error(data.error || data.message || 'Erro ao atualizar função.');
            }

            setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));

            await logAction(profile?.id, 'UPDATE_USER_ROLE', {
                target_user_id: userId,
                old_role: targetUser.role,
                new_role: newRole
            });
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'usuarios', acao: 'mudar_funcao_usuario', origem: 'acao' });
            addToast('Função atualizada com sucesso.', 'success');
        } catch (error: any) {
            console.error('Error updating role:', error);
            addToast(error.message || 'Erro ao atualizar permissão.', 'error');
        } finally {
            setUpdating(null);
        }
    };

    // Gerência comercial única (#6510): a gerente cuida também dos parceiros.
    const canEditParceiroVinculo = profile?.role === 'adm' || profile?.role === 'gerente';

    const canEditUserData = (target: Profile) => {
        if (!profile) return false;
        if (profile.role === 'adm') return true;
        if (target.id === profile.id) return false;
        if (profile.role === 'gerente') return gerenteAlcanca(target);
        if (profile.role === 'gerente_parcerias') return target.role === 'parceiro' || target.role === 'vendedor_parceiro';
        if (profile.role === 'parceiro') return target.role === 'vendedor_parceiro' && target.parceiro_id === profile.id;
        return false;
    };

    const openEditModal = (user: Profile) => {
        if (!canEditUserData(user)) {
            addToast('Você não tem permissão para editar este usuário.', 'error');
            return;
        }

        setEditingUser(user);
        setEditForm({
            full_name: user.full_name || '',
            whatsapp: formatWhatsapp(user.whatsapp || ''),
            job_title: user.job_title || '',
            email_corporativo: user.email_corporativo || '',
            email_pessoal: user.email_pessoal || '',
            cpf: user.cpf || '',
            captado_por_id: user.captado_por_id || '',
            parceiro_id: user.parceiro_id || ''
        });
    };

    const maskCPF = (value: string) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})/, '$1-$2')
            .replace(/(-\d{2})\d+?$/, '$1');
    };

    const getRoleOptionsForUser = (target: Profile): { value: UserRole; label: string }[] => {
        // Gerente de Parcerias foi aposentado (#6510): só aparece para quem ainda tem o papel.
        const allRoleOptions: { value: UserRole; label: string }[] = [
            { value: 'adm', label: 'ADM' },
            { value: 'gerente', label: 'Gerente Comercial' },
            ...(target.role === 'gerente_parcerias'
                ? [{ value: 'gerente_parcerias' as UserRole, label: 'Gerente de Parcerias (aposentado)' }]
                : []),
            { value: 'vendedor', label: 'Vendedor' },
            { value: 'parceiro', label: 'Parceiro' },
            { value: 'vendedor_parceiro', label: 'Vendedor Parceiro' },
            { value: 'analista', label: 'Analista' },
            { value: 'motoboy', label: 'Motoboy (Operador de Campo)' }
        ];

        if (!profile) return [{ value: target.role, label: target.role }];
        if (!canEditUserData(target)) {
            const current = allRoleOptions.find(r => r.value === target.role);
            return [current || { value: target.role, label: target.role }];
        }

        if (profile.role === 'adm') {
            return allRoleOptions;
        }

        if (profile.role === 'gerente') {
            return allRoleOptions.filter(r => (PAPEIS_ATRIBUIVEIS_PELO_GERENTE as readonly string[]).includes(r.value));
        }

        if (profile.role === 'gerente_parcerias') {
            return [
                { value: 'parceiro', label: 'Parceiro' },
                { value: 'vendedor_parceiro', label: 'Vendedor Parceiro' },
                { value: 'analista', label: 'Analista' }
            ];
        }

        if (profile.role === 'parceiro') {
            return [
                { value: 'vendedor_parceiro', label: 'Vendedor Parceiro' },
                { value: 'analista', label: 'Analista' }
            ];
        }

        return [{ value: target.role, label: target.role }];
    };

    const handleSaveUserData = async () => {
        if (!editingUser) return;

        const fullName = editForm.full_name.trim();
        if (!fullName) {
            addToast('Nome é obrigatório.', 'error');
            return;
        }

        const emailCorporativo = editForm.email_corporativo.trim();
        if (emailCorporativo && !emailCorporativo.toLowerCase().endsWith(CORP_EMAIL_DOMAIN)) {
            addToast(`O e-mail corporativo deve terminar em ${CORP_EMAIL_DOMAIN}.`, 'error');
            return;
        }

        setSavingEdit(true);
        try {
            const normalizedWhatsapp = formatWhatsapp(editForm.whatsapp.trim());
            const payload: Record<string, string | null> = {
                full_name: fullName,
                whatsapp: normalizedWhatsapp,
                job_title: editForm.job_title.trim(),
                email_corporativo: emailCorporativo,
                email_pessoal: editForm.email_pessoal.trim(),
                cpf: editForm.cpf.trim()
            };
            // Vínculo da rede (Nova Política Comercial) — só faz sentido para parceiros
            if (editingUser.role === 'parceiro') {
                payload.captado_por_id = editForm.captado_por_id || null;
            }
            // Parceiro responsável — só editável por adm e gerente comercial, e só para vendedor_parceiro
            if (editingUser.role === 'vendedor_parceiro' && canEditParceiroVinculo) {
                if (!editForm.parceiro_id) {
                    addToast('Selecione um parceiro responsável.', 'error');
                    setSavingEdit(false);
                    return;
                }
                payload.parceiro_id = editForm.parceiro_id;
            }

            const { data, error } = await supabase.functions.invoke('manage-users', {
                body: {
                    action: 'update_user_profile',
                    userId: editingUser.id,
                    updates: payload
                }
            });

            if (error) throw error;
            if (data && data.success === false) {
                throw new Error(data.error || data.message || 'Erro ao atualizar usuário.');
            }

            setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...payload } : u));
            await logAction(profile?.id, 'UPDATE_USER_PROFILE', {
                target_user_id: editingUser.id,
                full_name: payload.full_name,
                whatsapp: payload.whatsapp,
                job_title: payload.job_title
            });
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'usuarios', acao: 'atualizar_perfil_usuario', origem: 'acao' });

            addToast('Dados do usuário atualizados com sucesso.', 'success');
            setEditingUser(null);
        } catch (error) {
            console.error('Error updating user data:', error);
            addToast('Erro ao atualizar dados do usuário.', 'error');
        } finally {
            setSavingEdit(false);
        }
    };

    const handleDepartmentChange = async (userId: string, newDepartamentos: string[]) => {
        setUpdating(userId);
        try {
            const legacyDept = newDepartamentos[0] || '';
            await api.auth.updateProfile(userId, { departamento: legacyDept, departamentos: newDepartamentos });
            setUsers(prev => prev.map(u => u.id === userId
                ? { ...u, departamento: legacyDept, departamentos: newDepartamentos }
                : u
            ));

            await logAction(profile?.id, 'UPDATE_USER_DEPARTMENT', {
                target_user_id: userId,
                new_departments: newDepartamentos
            });
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'usuarios', acao: 'mudar_departamento_usuario', origem: 'acao' });
            addToast('Departamento(s) atualizado(s) com sucesso.', 'success');
        } catch (error) {
            console.error('Error updating department:', error);
            addToast('Erro ao atualizar departamento.', 'error');
        } finally {
            setUpdating(null);
            setOpenDeptId(null);
        }
    };

    // Unidade (Goiânia/Morrinhos) define a equipe no BI de eficiência do Painel
    // Executivo. Só adm mexe — é o mesmo público que enxerga o BI.
    const handleUnidadeChange = async (userId: string, escolhida: string) => {
        const unidade = escolhida === 'Goiânia' || escolhida === 'Morrinhos' ? escolhida : null;
        setUpdating(userId);
        try {
            await api.auth.updateProfile(userId, { unidade });
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, unidade } : u));
            await logAction(profile?.id, 'UPDATE_USER_UNIDADE', { target_user_id: userId, unidade });
            addToast('Unidade atualizada.', 'success');
        } catch (error) {
            console.error('Error updating unidade:', error);
            addToast('Erro ao atualizar a unidade.', 'error');
        } finally {
            setUpdating(null);
        }
    };

    // Função no Comercial (Closer, BDR, Hunter). É ela que decide quem recebe lead
    // pelo rodízio e em qual plano a negociação nasce — não o Cargo, que é texto
    // livre. Ao escolher, o Cargo passa a mostrar o nome da função.
    const handleFuncaoComercialChange = async (userId: string, escolhida: string) => {
        const funcao = (FUNCOES_COMERCIAIS as readonly string[]).includes(escolhida) ? escolhida as FuncaoComercial : null;
        const anterior = funcaoComercial(users.find(u => u.id === userId));
        const updates: Partial<Profile> = funcao
            ? { funcao_comercial: funcao, job_title: ROTULO_FUNCAO_COMERCIAL[funcao] }
            : { funcao_comercial: null };
        setUpdating(userId);
        try {
            await api.auth.updateProfile(userId, updates);
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updates } : u));
            await logAction(profile?.id, 'UPDATE_USER_FUNCAO_COMERCIAL', { target_user_id: userId, funcao_comercial: funcao, anterior });
            addToast(funcao ? `Função alterada para ${ROTULO_FUNCAO_COMERCIAL[funcao]}.` : 'Função no Comercial removida.', 'success');
        } catch (error: any) {
            console.error('Error updating funcao_comercial:', error);
            const faltaColuna = /funcao_comercial/.test(String(error?.message || ''));
            addToast(faltaColuna
                ? 'O campo Função no Comercial ainda não existe no banco: falta aplicar a migration 20261029140000_funcao_comercial.sql.'
                : 'Erro ao atualizar a função no Comercial.', 'error', 8000);
        } finally {
            setUpdating(null);
        }
    };

    // Tela da Visão Geral (#6493). Vazio = automática pelo papel e departamento
    // (src/lib/visaoGeral/perfis.ts). Só adm, como a unidade.
    const handleTelaInicialChange = async (userId: string, escolhida: string) => {
        const tela_inicial = ehPerfilVisaoGeral(escolhida) ? escolhida : null;
        setUpdating(userId);
        try {
            await api.auth.updateProfile(userId, { tela_inicial });
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, tela_inicial } : u));
            await logAction(profile?.id, 'UPDATE_USER_TELA_INICIAL', { target_user_id: userId, tela_inicial });
            addToast('Tela inicial atualizada.', 'success');
        } catch (error: any) {
            console.error('Error updating tela_inicial:', error);
            const faltaColuna = /tela_inicial/.test(String(error?.message || ''));
            addToast(faltaColuna
                ? 'O campo Tela inicial ainda não existe no banco: falta aplicar a migration 20261027210000_profiles_tela_inicial.sql.'
                : 'Erro ao atualizar a tela inicial.', 'error', 8000);
        } finally {
            setUpdating(null);
        }
    };

    const toggleDeptForUser = (userId: string, dept: string) => {
        const user = users.find(u => u.id === userId);
        if (!user) return;
        const current = user.departamentos || (user.departamento ? [user.departamento] : []);
        const next = current.includes(dept)
            ? current.filter(d => d !== dept)
            : [...current, dept];
        setUsers(prev => prev.map(u => u.id === userId
            ? { ...u, departamentos: next, departamento: next[0] || '' }
            : u
        ));
    };

    // Inativa ou exclui a conta pela edge manage-users. Lança erro com a causa — quem
    // mostra é a janela de pendências, que chama isto depois da escolha transferir/arquivar.
    const executarNaConta = async (acao: 'inativar' | 'excluir', alvo: Profile) => {
        const { data, error } = await supabase.functions.invoke('manage-users', {
            body: { action: acao === 'excluir' ? 'delete_user' : 'toggle_status', userId: alvo.id }
        });
        if (error) {
            const detalhe = await (error as any)?.context?.json?.().catch(() => null);
            throw new Error(detalhe?.error || error.message);
        }
        if (data && data.success === false) {
            throw new Error(data.error || data.message || 'Erro desconhecido no servidor');
        }

        if (acao === 'excluir') {
            setUsers(prev => prev.filter(u => u.id !== alvo.id));
            await logAction(profile?.id, 'DELETE_USER', { target_user_id: alvo.id, target_name: alvo.full_name });
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'usuarios', acao: 'excluir_usuario', origem: 'acao' });
        } else {
            setUsers(prev => prev.map(u => u.id === alvo.id ? { ...u, status: 'inactive' } : u));
            await logAction(profile?.id, 'UPDATE_USER_STATUS', { target_user_id: alvo.id, new_status: 'inactive' });
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'usuarios', acao: 'mudar_status_usuario', origem: 'acao' });
        }
    };

    const concluirSaida = ({ conta, transferido, para }: ResultadoSaida) => {
        const alvo = saida?.usuario;
        setSaida(null);
        if (!alvo) return;

        if (transferido) {
            logAction(profile?.id, 'TRANSFERIR_PENDENCIAS_USUARIO', {
                from_user_id: alvo.id, from_name: alvo.full_name,
                to_user_id: para?.id, to_name: para?.full_name,
                tarefas: transferido.tarefas, negociacoes: transferido.negociacoes,
            });
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'usuarios', acao: 'transferir_pendencias_usuario', origem: 'acao' });
        }

        const partes: string[] = [];
        if (conta === 'excluir') partes.push('Usuário excluído.');
        if (conta === 'inativar') partes.push(transferido ? 'Usuário inativado.' : 'Usuário inativado; as pendências ficaram arquivadas.');
        if (transferido) {
            partes.push(`${transferido.tarefas} tarefa(s) e ${transferido.negociacoes} negociação(ões) passaram para ${para?.full_name}.`);
        }
        addToast(partes.join(' ') || 'Nada mudou.', 'success', 7000);
    };

    const handleAction = async (action: 'toggle_status' | 'delete_user' | 'reset_password' | 'clear_user_logs', userId: string, userName?: string) => {
        setOpenMenuId(null); // Close menu

        // Inativar e excluir passam pela janela de pendências: transferir ou arquivar o
        // que está aberto no nome da pessoa. Reativar segue direto.
        const alvo = users.find(u => u.id === userId);
        if (alvo && (action === 'delete_user' || (action === 'toggle_status' && alvo.status !== 'inactive'))) {
            setSaida({ usuario: alvo, acao: action === 'delete_user' ? 'excluir' : 'inativar' });
            return;
        }

        const performAction = async () => {
            if (action === 'clear_user_logs') {
                setConfirmationModal(prev => ({ ...prev, isLoading: true }));
            }

            try {
                // Updated to handle 200 OK with success: false
                const { data, error } = await supabase.functions.invoke('manage-users', {
                    body: { action, userId }
                });

                if (error) throw error;
                if (data && data.success === false) {
                    throw new Error(data.error || data.message || 'Erro desconhecido no servidor');
                }

                if (action === 'toggle_status') {
                    setUsers(prev => prev.map(u => {
                        if (u.id === userId) {
                            return { ...u, status: u.status === 'active' ? 'inactive' : 'active' };
                        }
                        return u;
                    }));
                    await logAction(profile?.id, 'UPDATE_USER_STATUS', {
                        target_user_id: userId,
                        new_status: users.find(u => u.id === userId)?.status === 'active' ? 'inactive' : 'active'
                    });
                    logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'usuarios', acao: 'mudar_status_usuario', origem: 'acao' });
                    addToast(`Status do usuário atualizado.`, 'success');
                } else if (action === 'reset_password') {
                    addToast(`Email de redefinição de senha enviado para o usuário.`, 'success');
                    await logAction(profile?.id, 'RESET_USER_PASSWORD', { target_user_id: userId });
                    logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'usuarios', acao: 'resetar_senha_usuario', origem: 'acao' });
                } else if (action === 'clear_user_logs') {
                    addToast(`Logs de atividade do usuário limpos com sucesso.`, 'success');
                    await logAction(profile?.id, 'CLEAR_USER_LOGS', { target_user_id: userId, target_name: userName });
                    logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'usuarios', acao: 'limpar_logs_usuario', origem: 'acao' });
                }

            } catch (error: any) {
                console.error('Error performing action:', error);
                addToast(error?.message || 'Erro ao executar ação.', 'error', 7000);
            } finally {
                if (action === 'clear_user_logs') {
                    setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
                }
            }
        };

        if (action === 'clear_user_logs') {
            setConfirmationModal({
                isOpen: true,
                title: 'Limpar Logs de Atividade',
                description: `Tem certeza que deseja apagar todos os logs de atividade de ${userName}? Essa ação é necessária antes de excluir o usuário.`,
                confirmText: 'Sim, limpar logs',
                onConfirm: performAction,
                isLoading: false,
                variant: 'warning'
            });
        } else {
            // For other actions, just run it immediately (or add confirmation if desired)
            await performAction();
        }
    };


    if (profile?.role !== 'adm' && profile?.role !== 'parceiro' && profile?.role !== 'gerente_parcerias' && profile?.role !== 'gerente') {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-red-50 rounded-2xl border border-red-100 animate-fade-in">
                <Shield size={48} className="text-red-400 mb-4" />
                <h2 className="text-xl font-bold text-red-900 mb-2">Acesso Restrito</h2>
                <p className="text-red-700">Apenas administradores e gerentes podem gerenciar usuários.</p>
            </div>
        );
    }

    const filteredUsers = users.filter(u =>
        u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.departamento?.toLowerCase().includes(searchTerm.toLowerCase())
    ).filter(u => {
        if (profile?.role === 'adm') return true;
        if (profile?.role === 'parceiro') {
            return u.id === profile.id || u.parceiro_id === profile.id;
        }
        if (profile?.role === 'gerente_parcerias') {
            return u.role === 'parceiro' || u.role === 'vendedor_parceiro' || u.id === profile.id;
        }
        if (profile?.role === 'gerente') {
            return gerenteAlcanca(u) || u.id === profile.id;
        }
        return false;
    });

    return (
        <div className="space-y-6 animate-fade-in relative">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold font-poppins text-gray-900 dark:text-white">Usuários e Permissões</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Gerencie o acesso à plataforma.</p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-xl font-bold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                >
                    <Plus size={18} />
                    Adicionar Usuário
                </button>
            </div>

            {/* Search */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center gap-2 transition-colors">
                <Search size={20} className="text-gray-400" />
                <input
                    type="text"
                    placeholder="Buscar por nome ou função..."
                    className="flex-1 outline-none text-sm bg-transparent text-gray-900 dark:text-white placeholder:text-gray-400"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {loading ? (
                <LoadingSpinner label="Carregando usuários..." />
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-visible transition-colors">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-300 text-xs font-bold uppercase tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Usuário</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Função (Role)</th>
                                <th className="px-6 py-4">Departamento</th>
                                <th className="px-6 py-4 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {filteredUsers.map(user => (
                                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <img
                                                src={user.avatar_url || 'https://via.placeholder.com/40'}
                                                alt="Avatar"
                                                className={`w-10 h-10 rounded-full border border-gray-200 dark:border-gray-600 object-cover ${user.status === 'inactive' ? 'grayscale opacity-50' : ''}`}
                                                onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/40')}
                                            />
                                            <div>
                                                <p className={`font-semibold ${user.status === 'inactive' ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>{user.full_name}</p>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">{user.job_title || 'Sem cargo'}</p>
                                                    <p className="text-xs text-gray-400">{user.whatsapp || 'Sem WhatsApp'}</p>

                                                    {user.role === 'vendedor_parceiro' && user.parceiro_id && (() => {
                                                        const linkedUser = users.find(u => u.id === user.parceiro_id);
                                                        const isLinkedToGerente = linkedUser?.role === 'gerente_parcerias';
                                                        return (
                                                            <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border whitespace-nowrap ${isLinkedToGerente
                                                                ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-100 dark:border-purple-800'
                                                                : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-100 dark:border-indigo-800'
                                                                }`}>
                                                                <Users size={10} />
                                                                <span>{isLinkedToGerente ? 'Ger. Parcerias' : 'Parceiro'}: <span className="font-bold">{linkedUser?.full_name?.split(' ')[0] || 'Desconhecido'}</span></span>
                                                            </div>
                                                        );
                                                    })()}

                                                    {user.role === 'parceiro' && (
                                                        <div className="flex items-center gap-1 px-1.5 py-0.5 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded text-[10px] font-medium border border-teal-100 dark:border-teal-800 whitespace-nowrap">
                                                            <Users size={10} />
                                                            <span>Equipe: <span className="font-bold">{users.filter(u => u.parceiro_id === user.id).length} vend.</span></span>
                                                        </div>
                                                    )}

                                                    {user.role === 'gerente_parcerias' && (
                                                        <div className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded text-[10px] font-medium border border-purple-100 dark:border-purple-800 whitespace-nowrap">
                                                            <Users size={10} />
                                                            <span>Equipe: <span className="font-bold">{users.filter(u => u.parceiro_id === user.id).length} vend.</span></span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {user.status === 'inactive' ? (
                                            <span className="px-2 py-1 bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 rounded-full text-[10px] font-bold uppercase flex items-center gap-1 w-fit">
                                                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                                Inativo
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded-full text-[10px] font-bold uppercase flex items-center gap-1 w-fit">
                                                <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                                Ativo
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <select
                                            value={user.role}
                                            onChange={(e) => handleRoleChange(user.id, e.target.value as UserRole)}
                                            disabled={updating === user.id || user.status === 'inactive' || !canEditUserData(user)}
                                            className={`px-3 py-1.5 rounded-lg text-sm border-none font-bold uppercase disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors ${user.role === 'adm'
                                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                                                : user.role === 'gerente'
                                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                                    : user.role === 'gerente_parcerias'
                                                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                                                        : user.role === 'parceiro'
                                                            ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                                                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                                }`}
                                        >
                                            {getRoleOptionsForUser(user).map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                        {updating === user.id && <Loader2 size={12} className="inline ml-2 animate-spin text-gray-400" />}
                                    </td>
                                    <td className="px-6 py-4">
                                        {/* Multi-dept display + inline editor */}
                                        <div className="relative">
                                            {/* Badges display */}
                                            <div
                                                className={`flex flex-wrap gap-1 min-w-[120px] ${profile?.role === 'adm' || profile?.role === 'gerente'
                                                    ? 'cursor-pointer'
                                                    : 'cursor-default'
                                                    }`}
                                                onClick={() => {
                                                    if ((profile?.role === 'adm' || profile?.role === 'gerente') && user.id !== profile?.id && user.status !== 'inactive') {
                                                        setOpenDeptId(openDeptId === user.id ? null : user.id);
                                                    }
                                                }}
                                            >
                                                {(user.departamentos && user.departamentos.length > 0
                                                    ? user.departamentos
                                                    : user.departamento ? [user.departamento] : []
                                                ).map(d => (
                                                    <span key={d} className={`px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${DEPT_COLOR[d] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}>{d}</span>
                                                ))}
                                                {(!user.departamentos || user.departamentos.length === 0) && !user.departamento && (
                                                    <span className="text-xs text-gray-400 italic">Sem depto.</span>
                                                )}
                                            </div>
                                            {/* Dropdown */}
                                            {openDeptId === user.id && (
                                                <div className="absolute left-0 top-full mt-1 z-50 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                                                    {DEPARTAMENTOS.map(dept => {
                                                        const current = user.departamentos && user.departamentos.length > 0
                                                            ? user.departamentos
                                                            : user.departamento ? [user.departamento] : [];
                                                        const checked = current.includes(dept);
                                                        return (
                                                            <label key={dept} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                                                                <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${checked ? 'bg-primary border-primary' : 'border-gray-300 dark:border-gray-500'
                                                                    }`}>
                                                                    {checked && <Check size={10} className="text-white" strokeWidth={3} />}
                                                                </div>
                                                                <input type="checkbox" className="hidden" checked={checked} onChange={() => toggleDeptForUser(user.id, dept)} />
                                                                <span className="text-sm text-gray-700 dark:text-gray-200">{dept}</span>
                                                            </label>
                                                        );
                                                    })}
                                                    <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700 flex gap-2">
                                                        <button
                                                            onClick={() => setOpenDeptId(null)}
                                                            className="flex-1 text-xs text-gray-500 py-1 hover:text-gray-700"
                                                        >
                                                            Cancelar
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                const current = user.departamentos && user.departamentos.length > 0
                                                                    ? user.departamentos
                                                                    : user.departamento ? [user.departamento] : [];
                                                                handleDepartmentChange(user.id, current);
                                                            }}
                                                            disabled={updating === user.id}
                                                            className="flex-1 text-xs font-bold text-primary py-1 hover:text-primary/80 flex items-center justify-center gap-1"
                                                        >
                                                            {updating === user.id ? <Loader2 size={10} className="animate-spin" /> : null}
                                                            Salvar
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {(profile?.role === 'adm' || profile?.role === 'gerente') && (ehDoComercial(user) || funcaoComercial(user)) && (
                                            <select
                                                value={funcaoComercial(user) || ''}
                                                onChange={(e) => handleFuncaoComercialChange(user.id, e.target.value)}
                                                disabled={updating === user.id || user.status === 'inactive'}
                                                aria-label={`Função no Comercial de ${user.full_name}`}
                                                title="Função no Comercial — Closer recebe os leads do rodízio; BDR faz prospecção ativa e recebe as empresas visitadas pelo motoboy; Hunter vende na rua."
                                                className="mt-1.5 mr-1.5 px-2 py-1 rounded-lg text-[11px] border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 disabled:opacity-50 cursor-pointer dark:[color-scheme:dark]"
                                            >
                                                <option value="">Sem função no Comercial</option>
                                                {FUNCOES_COMERCIAIS.map(f => (
                                                    <option key={f} value={f}>Função: {ROTULO_FUNCAO_COMERCIAL[f]}</option>
                                                ))}
                                            </select>
                                        )}
                                        {profile?.role === 'adm' && (
                                            <select
                                                value={user.unidade || ''}
                                                onChange={(e) => handleUnidadeChange(user.id, e.target.value)}
                                                disabled={updating === user.id || user.status === 'inactive'}
                                                aria-label={`Unidade de ${user.full_name}`}
                                                title="Unidade — separa as equipes no BI de eficiência (Painel Executivo › Equipes)"
                                                className="mt-1.5 px-2 py-1 rounded-lg text-[11px] border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 disabled:opacity-50 cursor-pointer dark:[color-scheme:dark]"
                                            >
                                                <option value="">Sem unidade</option>
                                                <option value="Goiânia">Goiânia</option>
                                                <option value="Morrinhos">Morrinhos</option>
                                            </select>
                                        )}
                                        {profile?.role === 'adm' && (
                                            <select
                                                value={ehPerfilVisaoGeral(user.tela_inicial) ? user.tela_inicial : ''}
                                                onChange={(e) => handleTelaInicialChange(user.id, e.target.value)}
                                                disabled={updating === user.id || user.status === 'inactive'}
                                                aria-label={`Tela inicial de ${user.full_name}`}
                                                title="Tela inicial — a Visão Geral que a pessoa vê ao entrar. Automática = pelo papel e departamento."
                                                className="mt-1.5 ml-1.5 px-2 py-1 rounded-lg text-[11px] border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 disabled:opacity-50 cursor-pointer dark:[color-scheme:dark]"
                                            >
                                                <option value="">Tela: {DEFINICOES_PERFIL[perfilAutomatico(user)].rotulo} (automática)</option>
                                                {PERFIS_VISAO_GERAL.map(id => (
                                                    <option key={id} value={id}>Tela: {DEFINICOES_PERFIL[id].rotulo}</option>
                                                ))}
                                            </select>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-right relative">
                                        {(profile?.role === 'adm' || (profile?.role === 'parceiro' && user.id !== profile.id) || (profile?.role === 'gerente_parcerias' && user.id !== profile.id) || (profile?.role === 'gerente' && user.id !== profile.id)) && (
                                            <>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenMenuId(openMenuId === user.id ? null : user.id);
                                                    }}
                                                    className="text-gray-400 hover:text-primary p-2 rounded-full hover:bg-gray-100 transition-colors"
                                                >
                                                    <MoreVertical size={16} />
                                                </button>

                                                {/* Dropdown Menu */}
                                                {openMenuId === user.id && (
                                                    <div
                                                        ref={menuRef}
                                                        className="absolute right-0 top-12 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 z-50 animate-fade-in overflow-hidden"
                                                    >
                                                        <div className="py-1">
                                                            {canEditUserData(user) && (
                                                                <button
                                                                    onClick={() => {
                                                                        setOpenMenuId(null);
                                                                        openEditModal(user);
                                                                    }}
                                                                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                                >
                                                                    <Building2 size={16} />
                                                                    Editar Dados
                                                                </button>
                                                            )}
                                                            {profile?.role === 'adm' && (
                                                                <button
                                                                    onClick={() => {
                                                                        setOpenMenuId(null);
                                                                        setPermissionsModalUser(user);
                                                                    }}
                                                                    className="w-full text-left px-4 py-3 text-sm text-indigo-600 dark:text-indigo-400 flex items-center gap-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors"
                                                                >
                                                                    <Shield size={16} />
                                                                    Permissões
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleAction('toggle_status', user.id)}
                                                                className={`w-full text-left px-4 py-3 text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${user.status === 'inactive' ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}
                                                            >
                                                                {user.status === 'inactive' ? <CheckCircle size={16} /> : <Ban size={16} />}
                                                                {user.status === 'inactive' ? 'Ativar Usuário' : 'Inativar Usuário'}
                                                            </button>
                                                            {user.status === 'inactive' && (
                                                                <button
                                                                    onClick={() => {
                                                                        setOpenMenuId(null);
                                                                        setSaida({ usuario: user, acao: 'transferir' });
                                                                    }}
                                                                    className="w-full text-left px-4 py-3 text-sm text-purple-600 dark:text-purple-400 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                                >
                                                                    <Send size={16} />
                                                                    Transferir pendências
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleAction('reset_password', user.id)}
                                                                className="w-full text-left px-4 py-3 text-sm text-blue-600 dark:text-blue-400 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                            >
                                                                <KeyRound size={16} />
                                                                Redefinir Senha
                                                            </button>
                                                            <button
                                                                onClick={() => handleAction('clear_user_logs', user.id, user.full_name)}
                                                                className="w-full text-left px-4 py-3 text-sm text-yellow-600 dark:text-yellow-400 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                            >
                                                                <Activity size={16} />
                                                                Limpar Logs de Atividade
                                                            </button>
                                                            <hr className="border-gray-50 dark:border-gray-700" />
                                                            <button
                                                                onClick={() => handleAction('delete_user', user.id, user.full_name)}
                                                                className="w-full text-left px-4 py-3 text-sm text-red-600 dark:text-red-400 flex items-center gap-2 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                                                            >
                                                                <Trash2 size={16} />
                                                                Excluir Usuário
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmationModal.isOpen}
                onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
                onConfirm={confirmationModal.onConfirm}
                title={confirmationModal.title}
                description={confirmationModal.description}
                confirmText={confirmationModal.isLoading ? 'Processando...' : confirmationModal.confirmText || 'Confirmar'}
                isLoading={confirmationModal.isLoading}
                variant={confirmationModal.variant}
            />

            {saida && profile && (
                <PendenciasUsuarioModal
                    usuario={saida.usuario}
                    acao={saida.acao}
                    ator={profile}
                    perfis={users}
                    onClose={() => setSaida(null)}
                    executarNaConta={(acao) => executarNaConta(acao, saida.usuario)}
                    onConcluido={concluirSaida}
                />
            )}

            <CreateUserModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onSuccess={loadUsers}
            />

            {permissionsModalUser && (
                <PermissionsModal
                    isOpen={!!permissionsModalUser}
                    onClose={() => setPermissionsModalUser(null)}
                    userProfile={permissionsModalUser}
                    onSuccess={(updatedProfile) => {
                        setUsers(prev => prev.map(u => u.id === updatedProfile.id ? { ...u, permissions: updatedProfile.permissions } : u));
                    }}
                />
            )}

            {editingUser && ReactDOM.createPortal(
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
                    {...propsFundoModal(() => setEditingUser(null))}
                >
                    <div
                        className="w-full max-w-lg rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Editar usuário</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Atualize Nome, WhatsApp, Cargo/Função, E-mail Corporativo, E-mail Pessoal e CPF.</p>
                        </div>

                        <div className="px-6 py-4 space-y-4">
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome</label>
                                <input
                                    type="text"
                                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    value={editForm.full_name}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">WhatsApp</label>
                                <input
                                    type="text"
                                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    value={editForm.whatsapp}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, whatsapp: formatWhatsapp(e.target.value) }))}
                                    placeholder="(00) 00000-0000"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Cargo</label>
                                <input
                                    type="text"
                                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    value={editForm.job_title}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, job_title: e.target.value }))}
                                    placeholder="Ex.: Consultor Comercial"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">E-mail Corporativo</label>
                                <input
                                    type="email"
                                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    value={editForm.email_corporativo}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, email_corporativo: e.target.value }))}
                                    placeholder={`nome${CORP_EMAIL_DOMAIN}`}
                                />
                                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                    É o e-mail de login e o usado para assinar termos. Alterar aqui atualiza o acesso do usuário imediatamente. Deve terminar em <strong>{CORP_EMAIL_DOMAIN}</strong>.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">E-mail Pessoal</label>
                                    <input
                                        type="email"
                                        className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        value={editForm.email_pessoal}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, email_pessoal: e.target.value }))}
                                        placeholder="nome@gmail.com"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">CPF</label>
                                    <input
                                        type="text"
                                        className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        value={editForm.cpf}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, cpf: maskCPF(e.target.value) }))}
                                        placeholder="000.000.000-00"
                                        maxLength={14}
                                    />
                                </div>
                            </div>

                            {editingUser.role === 'vendedor_parceiro' && canEditParceiroVinculo && (
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Parceiro Responsável</label>
                                    <select
                                        className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        value={editForm.parceiro_id}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, parceiro_id: e.target.value }))}
                                    >
                                        <option value="">Selecione...</option>
                                        {profile?.role === 'gerente_parcerias' && (
                                            <option value={profile.id}>🔷 Eu mesmo (Gerente de Parcerias)</option>
                                        )}
                                        {users.filter(u => u.role === 'parceiro').map(p => (
                                            <option key={p.id} value={p.id}>👤 {p.full_name} (Parceiro)</option>
                                        ))}
                                    </select>
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                        Define de qual parceiro este vendedor faz parte.
                                    </p>
                                </div>
                            )}

                            {editingUser.role === 'parceiro' && MOSTRAR_ITENS_V2_EM_DISCUSSAO && (
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Captado por (vendedor externo)</label>
                                    <select
                                        className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        value={editForm.captado_por_id}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, captado_por_id: e.target.value }))}
                                    >
                                        <option value="">Sem vínculo</option>
                                        {users.filter(u => u.role === 'vendedor' && u.status === 'active').map(v => (
                                            <option key={v.id} value={v.id}>{v.full_name}</option>
                                        ))}
                                    </select>
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                        Define de quem é a rede deste parceiro na Nova Política Comercial (bonificação indireta + SLA de captação).
                                    </p>
                                </div>
                            )}

                        </div>

                        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                                onClick={() => setEditingUser(null)}
                                disabled={savingEdit}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2"
                                onClick={handleSaveUserData}
                                disabled={savingEdit}
                            >
                                {savingEdit ? <Loader2 size={14} className="animate-spin" /> : null}
                                Salvar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default UserManagement;
